import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Switch, useTheme } from "@mui/material";
import AlertsAPI from "../../../api/AlertsAPI";
import { CustomerAlertTypeSetting } from "../../../../../models/Alerts/CustomerAlertSettings";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";

const alertsAPI = new AlertsAPI();

/** Outcome of the last switch change, shown under that type's row. */
interface ToggleResult {
  code: string;
  isError: boolean;
  message: string;
}

interface CustomerAlertTypesPanelProps {
  customerId: number;
  /** View-only users see the switches but cannot change them. */
  disabled?: boolean;
}

/**
 * Lists the client alert types for one customer with an on/off switch each.
 *
 * Shown inside the customer form's Alerts card while the master switch is on. Each switch
 * saves immediately (`PUT /v1/alerts/admin/customers/:id/settings/types/:code`), separately
 * from the form's Save, and the change is written to the customer's activity log.
 */
const CustomerAlertTypesPanel: React.FC<CustomerAlertTypesPanelProps> = ({
  customerId,
  disabled = false,
}) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const [types, setTypes] = useState<CustomerAlertTypeSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [loadError, setLoadError] = useState("");
  const [toggleResult, setToggleResult] = useState<ToggleResult | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const settings = await alertsAPI.getCustomerSettings(customerId);
      setTypes(settings.types);
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : "Failed to load client alert settings.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId > 0) void load();
  }, [customerId, load]);

  const handleToggle = async (
    type: CustomerAlertTypeSetting,
    isEnabled: boolean,
  ) => {
    setSavingCode(type.code);
    setToggleResult(null);
    try {
      const settings = await alertsAPI.setCustomerAlertType(
        customerId,
        type.code,
        isEnabled,
      );
      setTypes(settings.types);
      setToggleResult({
        code: type.code,
        isError: false,
        message: `Turned ${isEnabled ? "on" : "off"}.`,
      });
    } catch (error) {
      setToggleResult({
        code: type.code,
        isError: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to save client alert setting.",
      });
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <CustomTypography variant="heading8" color={c.navyText} sx={{ mb: 0.5 }}>
        Client alert types
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={c.navyText}
        sx={{ mb: 1.5, opacity: 0.75 }}
      >
        All types are on by default. Changes save immediately. The Alert switch
        above applies when you save the customer.
      </CustomTypography>

      <InlineFormError message={loadError} />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? null : types.length === 0 ? (
        <CustomTypography variant="body2" color={c.navyText}>
          No client alert types are configured yet.
        </CustomTypography>
      ) : (
        types.map((type) => (
          <Box
            key={type.code}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <CustomTypography variant="heading8" color={c.darkBlueText}>
                {type.description || type.code}
              </CustomTypography>
              <SkuCopyRow
                sku={type.code}
                copyLabel="Copy code"
                variant="body3"
                color={c.navyText}
              />
              {toggleResult?.code === type.code &&
                (toggleResult.isError ? (
                  <InlineFormError
                    message={toggleResult.message}
                    sx={{ mt: 0.5, mb: 0 }}
                  />
                ) : (
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.success.main}
                  >
                    {toggleResult.message}
                  </CustomTypography>
                ))}
            </Box>
            {savingCode === type.code ? (
              <CircularProgress size={20} sx={{ mx: 1.5 }} />
            ) : (
              <Switch
                checked={type.is_enabled}
                onChange={(event) =>
                  void handleToggle(type, event.target.checked)
                }
                disabled={disabled || savingCode !== null}
                inputProps={{
                  "aria-label": `${type.description || type.code} alert`,
                }}
              />
            )}
          </Box>
        ))
      )}
    </Box>
  );
};

export default CustomerAlertTypesPanel;
